import * as React from "react";
import { graphql, compose } from "react-apollo";
import gql from "graphql-tag";
import {
  Select,
  FormControl,
  MenuItem,
  TextField,
  Button,
  InputLabel,
} from "@material-ui/core";
import Grid, { GridProps } from "@material-ui/core/Grid";
import TopBar from "./Ops.ApplicationStatusMessages.GeneralTopBar";
import handleMutationError from "app/utils/handleMutationError";
import { withRouter } from "react-router-dom";

import StatusMessageCategories from "app/data/statusMessageCategories";

const Levels = [
  {
    value: "error",
    label: "Error",
  },
  {
    value: "warning",
    label: "Warning",
  },
  {
    value: "info",
    label: "Info",
  },
];

type Props = {
  createStatusMessage: any;
  history: any;
  editMode: boolean;
  category: string;
  level: string;
  messageBody: string;
  id: string;
};

type State = {
  saving: boolean;
  categoryInput: String;
  levelInput: String;
  messageBodyInput: String;
  id: String;
};

class GridKey extends React.Component<GridProps> {
  render() {
    return (
      <Grid item={true} md={2} xs={12} {...this.props}>
        {this.props.children}
      </Grid>
    );
  }
}

class GridValue extends React.Component<GridProps> {
  render() {
    return (
      <Grid item={true} md={4} xs={12} {...this.props}>
        {this.props.children}
      </Grid>
    );
  }
}

class Create extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      saving: false,
      categoryInput: this.props.category ? this.props.category : "",
      levelInput: this.props.level ? this.props.level : "",
      messageBodyInput: this.props.messageBody ? this.props.messageBody : "",
      id: this.props.id ? this.props.id : "",
    };
  }

  hasRequiredInputs = (): boolean => {
    return (
      this.state.categoryInput &&
      this.state.levelInput.length &&
      this.state.messageBodyInput.length > 0
    );
  };

  onCategoryChange = (value: string) => {
    this.setState({
      categoryInput: value,
    });
  };

  onLevelChange = (value: string) => {
    this.setState({
      levelInput: value,
    });
  };

  onMessageBodyChange = (value: string) => {
    this.setState({
      messageBodyInput: value,
    });
  };

  onClear = () => {
    this.setState({
      categoryInput: "",
      levelInput: "",
      messageBodyInput: "",
    });
  };

  navigateToList = () => {
    this.props.history.push(`/snapscan/ops/application_status_messages/list`);
  };

  onSave = (addAnother = false) => {
    this.setState({ saving: true });
    this.props
      .createStatusMessage({
        variables: {
          id: this.state.id ? this.state.id : null,
          category: this.state.categoryInput,
          level: this.state.levelInput,
          message_body: this.state.messageBodyInput,
        },
      })
      .then(() => this.setState({ saving: false }))
      .catch((error) => {
        handleMutationError(error);
      })
      .finally(() => (addAnother ? this.onClear() : this.navigateToList()));
  };

  render() {
    return (
      <>
        <TopBar />
        <section className="padded">
          <div>
            {this.props.editMode ? (
              <h1 style={{ margin: 0 }}>Edit status message</h1>
            ) : (
              <h1 style={{ margin: 0 }}>Create new status message</h1>
            )}
          </div>

          <Grid
            alignItems="center"
            container={true}
            spacing={24}
            style={{ paddingTop: "20px" }}
          >
            <GridKey>
              <b style={{ fontWeight: 400 }}>Category:</b>
            </GridKey>
            <GridValue>
              <FormControl>
                <InputLabel htmlFor="category">Choose a category</InputLabel>
                <Select
                  style={{ width: "200px", padding: "10px" }}
                  value={this.state.categoryInput}
                  onChange={(event) =>
                    this.onCategoryChange(event.target.value)
                  }
                  inputProps={{
                    name: "categoryInput",
                    id: "category",
                  }}
                >
                  {StatusMessageCategories.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridValue>
          </Grid>

          <Grid
            alignItems="center"
            container={true}
            spacing={24}
            style={{ paddingTop: "20px" }}
          >
            <GridKey>
              <b style={{ fontWeight: 400 }}>Level:</b>
            </GridKey>
            <GridValue>
              <FormControl>
                <InputLabel htmlFor="level">Choose a level</InputLabel>
                <Select
                  style={{ width: "200px", padding: "10px" }}
                  value={this.state.levelInput}
                  onChange={(event) => this.onLevelChange(event.target.value)}
                  inputProps={{
                    name: "levelInput",
                    id: "level",
                  }}
                >
                  {Levels.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridValue>
          </Grid>

          <Grid
            alignItems="center"
            container={true}
            spacing={24}
            style={{ paddingTop: "50px" }}
          >
            <GridKey>
              <b style={{ fontWeight: 400 }}>Message body:</b>
            </GridKey>
            <GridValue>
              <TextField
                style={{ width: "100%" }}
                type={"text"}
                multiline
                value={this.state.messageBodyInput}
                onChange={(event) =>
                  this.onMessageBodyChange(event.target.value)
                }
              />
            </GridValue>
          </Grid>

          <div
            style={{
              paddingTop: "50px",
            }}
          >
            <Button
              className="button no-margin"
              variant="contained"
              color="primary"
              style={{
                marginRight: "15px",
              }}
              disabled={!this.hasRequiredInputs() || this.state.saving}
              onClick={() => {
                this.onSave();
              }}
            >
              {this.state.saving ? "Saving..." : "Save"}
            </Button>

            {this.props.editMode ? null : (
              <Button
                className="button no-margin"
                variant="contained"
                color="primary"
                style={{
                  marginRight: "15px",
                }}
                disabled={!this.hasRequiredInputs() || this.state.saving}
                onClick={() => {
                  this.onSave(true);
                }}
              >
                {this.state.saving ? "Saving..." : "Save and add another"}
              </Button>
            )}

            <Button
              className="button no-margin"
              variant="contained"
              color="primary"
              style={{
                marginRight: "15px",
              }}
              onClick={this.onClear}
            >
              Clear
            </Button>

            <Button
              className="button no-margin"
              variant="contained"
              color="primary"
              onClick={this.navigateToList}
            >
              Cancel
            </Button>
          </div>
        </section>
      </>
    );
  }
}

const createStatusMessage = graphql(
  gql`
    mutation createStatusMessage(
      $category: String!
      $level: String!
      $message_body: String!
      $id: ID
    ) {
      create_status_message(
        category: $category
        level: $level
        message_body: $message_body
        id: $id
      ) {
        id
      }
    }
  `,
  { name: "createStatusMessage" }
);

export default withRouter(compose(createStatusMessage)(Create));
